import React, { useState, useEffect } from 'react';
import type { BulkEmailDTO } from '../../services/apiClient';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import useCommunicationLists from '../../hooks/useCommunicationLists';
import handleChange from '../../utils/formUtils/handleChange';
import FileUpload from '../common/FileUpload';
import Button from '../../components/common/Button';
import { FileTypeConstants } from '../../constants/FileTypeConstants';
import { useFileApi } from '../../hooks/useFileApi';

const CUSTOM_LIST_VALUE = '__custom_upload__';

interface BulkEmailFormProps {
  initialValues: BulkEmailDTO;
  isSubmitting: boolean;
  setInitialValues: (values: BulkEmailDTO) => void;
  setAttachmentFile: (file: File | null) => void;
  onSubmit: (data: BulkEmailDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  list: {
    id: 'communication-list',
    title: 'Communication List',
  },
  content: {
    id: 'email-content',
    title: 'Email Content',
  },
  attachment: {
    id: 'attachment',
    title: 'Attachment',
  },
} as const;

const BulkEmailForm: React.FC<BulkEmailFormProps> = ({
  initialValues,
  isSubmitting,
  setInitialValues,
  setAttachmentFile,
  onSubmit,
  onCancel,
}) => {
  const { communicationLists, isLoading, error } = useCommunicationLists();
  const { uploadFile } = useFileApi();
  const [isUploadingCustomList, setIsUploadingCustomList] = useState(false);
  const [replacementValues, setReplacementValues] = useState<
    Record<string, string>
  >({});
  const [formData, setFormData] = useState(initialValues);

  // Sync formData with initialValues when they change (important for Edit mode)
  useEffect(() => {
    setFormData(initialValues);
  }, [initialValues]);


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files.length > 0) {
      setAttachmentFile(e.target.files[0]);
    }
  };

  const handleCustomListFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsUploadingCustomList(true);
      const response = await uploadFile('bulk-email-custom-lists', file.name, file, 'custom-lists');
      if (response?.success && response.data) {
        setFormData(prev => ({ ...prev, ...(({ customListFileRecordId: response.data }) as any) }));
      }
    } catch (err) {
      console.error('Error uploading custom list:', err);
    } finally {
      setIsUploadingCustomList(false);
    }
  };

  const handleDeleteAttachment = (): void => {
    setInitialValues({ ...formData, fileRecordId: undefined });
    setAttachmentFile(null); // Clear the file input
  };

  const updateReplacementValues = (
    communicationListId: string | undefined,
  ): void => {
    const selectedCommunicationList = (communicationLists as any[]).find(
      (list) => list.id === communicationListId,
    );

    if (selectedCommunicationList?.metadata) {
      try {
        const metadata = JSON.parse(selectedCommunicationList.metadata);
        const values = metadata?.ReplacementValues || {};
        setReplacementValues(values);
      } catch (error) {
        console.error('Failed to parse metadata:', error);
        setReplacementValues({});
      }
    } else {
      setReplacementValues({});
    }
  };

  useEffect(() => {
    updateReplacementValues(formData.communicationListId);
  }, [formData.communicationListId, communicationLists]);

  useEffect(() => {
    if (!formData.communicationListId && communicationLists.length > 0) {
      const defaultCommunicationListId = (communicationLists[0] as any)?.id;
      setFormData({
        ...formData,
        communicationListId: defaultCommunicationListId,
      });
    }
  }, [communicationLists]);

  const handleSave = () => {
    onSubmit(formData);
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
    >
      <FormSection
        id={FORM_SECTIONS.list.id}
        title={FORM_SECTIONS.list.title}
      >
        {isLoading ? (
          <p>Loading communication lists...</p>
        ) : error ? (
          <p>Error loading communication lists: {error}</p>
        ) : (
          <>
            <FormField
              id="communicationListId"
              label="Communication List"
              type="dropdown"
              value={formData.communicationListId || ''}
              onChange={(e) => {
                handleChange(e, setFormData);
              }}
              options={[
                ...communicationLists.map((list: any) => ({
                  value: list.id,
                  label: list.name,
                })),
                { value: CUSTOM_LIST_VALUE, label: 'Custom List (Excel)' },
              ]}
              placeholder="Select a communication list"
            />
            {formData.communicationListId === CUSTOM_LIST_VALUE && (
              <div className="mb-4 p-4 bg-gray-100 dark:bg-slate-800 rounded shadow">
                <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">
                  Custom List File Requirements:
                </h3>
                <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-400">
                  <li>
                    The file must have a column named{' '}
                    <strong className="text-gray-800 dark:text-gray-200">
                      IdPassportNumber
                    </strong>{' '}
                    containing the member ID/passport number used to look up
                    each recipient.
                  </li>
                  <li>
                    Any other column can be given a plain name (e.g.{' '}
                    <strong className="text-gray-800 dark:text-gray-200">
                      FirstName
                    </strong>
                    ) — no brackets needed in the file itself.
                  </li>
                  <li>
                    In the email content, wrap that same name in square
                    brackets to use it as a placeholder, e.g.{' '}
                    <strong className="text-gray-800 dark:text-gray-200">
                      [FirstName]
                    </strong>
                    . It will be replaced with that recipient&apos;s value
                    for that column. The brackets prevent the word from
                    being replaced anywhere else it naturally appears in the
                    email.
                  </li>
                </ul>
              </div>
            )}
          </>
        )}
        {formData.communicationListId === CUSTOM_LIST_VALUE && (
          <div className="md:col-span-2">
            <FileUpload
              id="customListFile"
              title="Upload custom recipient list"
              accept={['.xlsx', '.xls']}
              onChange={handleCustomListFileChange}
              isUploading={isUploadingCustomList}
            />
          </div>
        )}
        {replacementValues && Object.keys(replacementValues).length > 0 && (
          <div className="mb-4 p-4 bg-gray-100 dark:bg-slate-800 rounded shadow">
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">
              Available Replacement Values:
            </h3>
            <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-400">
              {Object.entries(replacementValues).map(([placeholder]) => (
                <li key={placeholder}>
                  <strong className="text-gray-800 dark:text-gray-200">
                    {placeholder}
                  </strong>
                </li>
              ))}
            </ul>
          </div>
        )}
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.content.id}
        title={FORM_SECTIONS.content.title}
      >
        <FormField
          id="subject"
          label="Subject"
          type="text"
          value={formData.subject || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter email subject"
        />
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter title"
        />
        <FormField
          id="content"
          label="Content"
          type="richtext"
          value={formData.content || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter email content"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.attachment.id}
        title={FORM_SECTIONS.attachment.title}
        columns={1}
      >
        <div className="mb-4">
          <label
            htmlFor="fileRecordId"
            className="block text-sm font-medium text-gray-700"
          >
            Attachment
          </label>
          {formData.fileRecordId ? (
            <div className="flex items-center space-x-4 mt-2">
              <span className="text-sm text-gray-600">
                Attachment uploaded (ID: {formData.fileRecordId})
              </span>
              <Button
                variant="danger"
                onClick={handleDeleteAttachment}
                className="text-sm"
              >
                Remove
              </Button>
            </div>
          ) : (
            <FileUpload
              id="fileRecordId"
              title="Upload or drag and drop the attachment."
              accept={FileTypeConstants.ALL_SUPPORTED}
              onChange={handleFileChange}
            />
          )}
        </div>
      </FormSection>
    </Form>
  );
};

export default BulkEmailForm;
